import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { DateInput, Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { VoidDialog } from '../../components/ui/VoidDialog';

interface PaymentRow {
  id: string;
  doc_no: string;
  doc_date: string;
  direction: string;
  mode: string;
  amount: string;
  allocated: string;
  party_name: string;
  reference_no: string | null;
  status: string;
  kind?: 'SETTLEMENT' | 'ADVANCE' | 'ADVANCE_REFUND';
}

/**
 * Money in from customers, money out to suppliers.
 *
 * A receipt with nothing chosen is applied to the oldest open invoice first,
 * which is what a shop does anyway. Choosing which invoices it settles needs a
 * separate permission, because that choice is how an aged debt gets hidden.
 */
export function PaymentsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState<'IN' | 'OUT' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [voiding, setVoiding] = useState<PaymentRow | null>(null);
  const [posted, setPosted] = useState<{ docNo: string; allocated: string; unallocated: string } | null>(null);
  const [form, setForm] = useState({
    docDate: today(),
    partyId: '',
    mode: 'CASH',
    amount: '',
    referenceNo: '',
    narration: '',
    branchId: '',
  });

  // A cashier may take a receipt without seeing the accounts: the list of past
  // payments and the supplier side are only asked for by roles that may see them.
  const seesList = can('accounts.view');
  const paysSuppliers = can('supplier.view');
  const list = useQuery({
    queryKey: ['payments'],
    queryFn: () => api.get<{ data: PaymentRow[] }>('/payments'),
    enabled: seesList,
  });
  const takesFromCustomers = can('customer.view');
  const customers = useQuery({
    queryKey: ['customers', ''],
    queryFn: () => endpoints.parties.customers(),
    enabled: takesFromCustomers,
  });
  const suppliers = useQuery({
    queryKey: ['suppliers', ''],
    queryFn: () => endpoints.parties.suppliers(),
    enabled: paysSuppliers,
  });

  const parties = (
    open === 'OUT'
      ? ((suppliers.data?.data ?? []) as unknown as { id: string; name: string }[])
      : ((customers.data?.data ?? []) as unknown as { id: string; name: string }[])
  ).map((p) => ({ value: p.id, label: p.name }));

  const create = useMutation({
    mutationFn: () =>
      api.post<{ docNo: string; allocated: string; unallocated: string }>(
        '/payments',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          direction: open,
          partyType: open === 'OUT' ? 'SUPPLIER' : 'CUSTOMER',
          partyId: form.partyId,
          mode: form.mode,
          amount: form.amount,
          ...(form.referenceNo ? { referenceNo: form.referenceNo } : {}),
          ...(form.narration ? { narration: form.narration } : {}),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (result) => {
      toast(
        `${result.docNo} recorded`,
        Number(result.allocated) > 0
          ? `${money(result.allocated)} applied to open invoices, oldest first`
          : 'Held on account — nothing outstanding to apply it to',
      );
      setPosted(result);
      setOpen(null);
      setForm({ ...form, partyId: '', amount: '', referenceNo: '', narration: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const rows = list.data?.data ?? [];

  const voidPayment = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/payments/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast(`${voiding?.doc_no ?? 'Payment'} voided`, 'Any invoices it had settled are open again.');
      void client.invalidateQueries({ queryKey: ['payments'] });
      void client.invalidateQueries({ queryKey: ['sales'] });
      setVoiding(null);
    },
    onError: (e) => {
      setError(e);
      setVoiding(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Payments and receipts"
        subtitle="Money in from customers, money out to suppliers."
        actions={
          can('payment.create') ? (
            <>
              {takesFromCustomers ? (
                <button className="btn-primary" onClick={() => setOpen('IN')}>
                  Receive money
                </button>
              ) : null}
              {paysSuppliers ? (
                <button className="btn-ghost" onClick={() => setOpen('OUT')}>
                  Pay somebody
                </button>
              ) : null}
            </>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {posted ? (
        <div className="mb-4 rounded border border-success/30 bg-success/5 p-3 text-sm">
          <span className="num font-medium">{posted.docNo}</span> recorded.{' '}
          {Number(posted.allocated) > 0 ? (
            <>
              <span className="num">{money(posted.allocated)}</span> applied to open invoices, oldest first
              {Number(posted.unallocated) > 0 ? (
                <>
                  , <span className="num">{money(posted.unallocated)}</span> left on account
                </>
              ) : null}
              .
            </>
          ) : (
            'Held on account — there was nothing outstanding to apply it to.'
          )}
        </div>
      ) : null}

      {!seesList ? (
        <p className="text-sm text-ink-soft">
          Take a customer&rsquo;s payment with <span className="font-medium">Receive money</span>. The list of past
          receipts and payments is kept for the accounts team.
        </p>
      ) : null}
      {seesList && list.isLoading ? <Loading /> : null}
      {seesList && !list.isLoading && rows.length === 0 ? (
        <Empty
          message="Nothing received or paid yet."
          hint="Money in from customers goes to their oldest open invoice first; money out reduces what you owe a supplier."
          action={can('payment.create') ? { label: 'Receive money', onClick: () => setOpen('IN') } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Document', 'Direction', 'Party', 'Mode', 'Reference', 'Amount', 'Applied', '']}>
          {rows.map((p) => (
            <tr key={p.id} className={p.status === 'CANCELLED' ? 'opacity-60' : ''}>
              <td className="td">{date(p.doc_date)}</td>
              <td className="td num">
                {p.kind && p.kind !== 'SETTLEMENT' ? (
                  <Link className="underline" to={`/advances/${p.id}/print`}>
                    {p.doc_no}
                  </Link>
                ) : (
                  p.doc_no
                )}
              </td>
              <td className="td">
                {p.direction === 'IN' ? (
                  <span className="text-success">received</span>
                ) : (
                  <span className="text-danger">paid</span>
                )}
                {p.kind === 'ADVANCE' ? <span className="chip-mute ml-1.5">advance</span> : null}
                {p.kind === 'ADVANCE_REFUND' ? <span className="chip-warn ml-1.5">advance refund</span> : null}
              </td>
              <td className="td">{p.party_name}</td>
              <td className="td text-xs">{p.mode.toLowerCase()}</td>
              <td className="td num text-xs">{p.reference_no ?? '—'}</td>
              <td className="td num text-right font-medium">{money(p.amount)}</td>
              <td className="td num text-right text-ink-soft">
                {p.kind && p.kind !== 'SETTLEMENT' ? (
                  <span className="text-2xs">held as advance</span>
                ) : Number(p.allocated) < Number(p.amount) ? (
                  <span title="Part of this is still on account">{money(p.allocated)}</span>
                ) : (
                  money(p.allocated)
                )}
              </td>
              <td className="td text-right">
                {p.status === 'CANCELLED' ? (
                  <span className="text-xs text-ink-soft">voided</span>
                ) : can('document.void') ? (
                  <button className="text-xs text-danger underline" onClick={() => setVoiding(p)}>
                    Void
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title={open === 'IN' ? 'Receive money' : 'Pay somebody'}
          subtitle={
            open === 'IN'
              ? 'Applied to the oldest open invoice first, and anything left over stays on account.'
              : 'Reduces what the shop owes them.'
          }
          onClose={() => setOpen(null)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label={open === 'IN' ? 'Customer' : 'Supplier'} required>
              <Select value={form.partyId} onChange={(v) => setForm({ ...form, partyId: v })} options={parties} />
            </Field>

            <Field label="Date" required>
              <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount" required>
                <NumberInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} suffix="₹" />
              </Field>
              <Field label="Mode" required>
                <Select
                  value={form.mode}
                  onChange={(v) => setForm({ ...form, mode: v })}
                  options={[
                    { value: 'CASH', label: 'Cash' },
                    { value: 'BANK', label: 'Bank transfer' },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'CARD', label: 'Card' },
                    { value: 'CHEQUE', label: 'Cheque' },
                  ]}
                />
              </Field>
            </div>

            <Field label="Reference" hint="Cheque number, UPI reference, NEFT number.">
              <TextInput value={form.referenceNo} onChange={(v) => setForm({ ...form, referenceNo: v })} />
            </Field>

            <Field label="Note">
              <TextInput value={form.narration} onChange={(v) => setForm({ ...form, narration: v })} />
            </Field>

            {branches.length > 1 ? (
              <Field label="Branch" required>
                <Select
                  value={form.branchId || (defaultBranchId ?? '')}
                  onChange={(v) => setForm({ ...form, branchId: v })}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" disabled={!form.partyId || !form.amount || create.isPending}>
                {create.isPending ? 'Recording…' : open === 'IN' ? 'Receive' : 'Pay'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOpen(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
      <VoidDialog
        docNo={voiding?.doc_no ?? null}
        what={voiding?.direction === 'IN' ? 'receipt' : 'payment'}
        effect={
          voiding?.direction === 'IN'
            ? 'The money comes back out of cash or bank, and any invoices it settled go back on the customer’s outstanding list.'
            : 'The money returns to cash or bank, and the supplier is owed it again.'
        }
        busy={voidPayment.isPending}
        onCancel={() => setVoiding(null)}
        onConfirm={(reason) => voiding && voidPayment.mutate({ id: voiding.id, reason })}
      />
    </>
  );
}
